import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { useTrainingGoals } from '@/features/calendar/hooks/useTrainingGoals';
import { CreateTrainingGoalData, UpdateTrainingGoalData } from '@/types/trainingGoals';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { SossietyDialogHeader, SossietyDialogClose } from '@/components/ui/SossietyDialogHeader';
import { useI18n } from '@/contexts/I18nContext';

interface TrainingGoalModalProps {
  isOpen: boolean;
  onClose: () => void;
  isEditing?: boolean;
  currentGoal?: number;
}

export const TrainingGoalModal: React.FC<TrainingGoalModalProps> = ({
  isOpen,
  onClose,
  isEditing = false,
  currentGoal
}) => {
  const [step, setStep] = useState(1);
  const [monthlyGoal, setMonthlyGoal] = useState(currentGoal?.toString() || '');
  const [error, setError] = useState('');

  const { createGoal, updateGoal, isCreating, isUpdating } = useTrainingGoals();
  const { t } = useI18n();

  useEffect(() => {
    if (isOpen) {
      setStep(1);
      setMonthlyGoal(currentGoal?.toString() || '');
      setError('');
    }
  }, [isOpen, currentGoal]);

  const handleNext = () => {
    if (!monthlyGoal || parseInt(monthlyGoal) < 1) {
      setError(t('home.training_goal.invalid_goal_error'));
      return;
    }
    setStep(2);
    setError('');
  };

  const handleBack = () => {
    setStep(1);
    setError('');
  };

  const handleConfirm = async () => {
    try {
      const goalNumber = parseInt(monthlyGoal);
      if (isEditing) {
        await updateGoal({ monthly_goal: goalNumber });
      } else {
        await createGoal({ monthly_goal: goalNumber });
      }
      onClose();
    } catch (error) {
      setError(t('home.training_goal.save_error'));
    }
  };

  const weeklyGoal = monthlyGoal ? Math.ceil(parseInt(monthlyGoal) / 4) : 0;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent 
        className="w-[90vw] max-w-md bg-white rounded-2xl p-6"
        aria-describedby="training-goal-description"
      >
        <DialogTitle className="sr-only">
          {isEditing ? t('home.training_goal.edit_title') : t('home.training_goal.set_title')}
        </DialogTitle>
        <div id="training-goal-description" className="sr-only">
          {isEditing ? t('home.training_goal.edit_description') : t('home.training_goal.set_description')}
        </div>
        <SossietyDialogHeader title={isEditing ? t('home.training_goal.edit_title') : t('home.training_goal.set_title')}>
          <SossietyDialogClose onClick={onClose} />
        </SossietyDialogHeader>

        {step === 1 ? (
          // Step 1: Goal Input
          <div className="space-y-6">
            <div className="text-center">
              <h2 className="text-xl font-bold text-black mb-2">
                {t('home.training_goal.monthly_goal')}
              </h2>
              <p className="text-gray-600 text-sm">
                {t('home.training_goal.modal_description')}
              </p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-black text-sm font-medium mb-2">
                  {t('home.training_goal.monthly_training_goal')}
                </label>
                <Input
                  type="number"
                  value={monthlyGoal}
                  onChange={(e) => setMonthlyGoal(e.target.value)}
                  placeholder={t('home.training_goal.placeholder')}
                  className="bg-gray-50 border-gray-300 text-black placeholder:text-gray-500"
                  min="1"
                />
                {error && (
                  <p className="text-red-500 text-sm mt-1">{error}</p>
                )}
              </div>

              <Button
                onClick={handleNext}
                disabled={!monthlyGoal || parseInt(monthlyGoal) < 1}
                className="w-full bg-sossiety-red hover:bg-sossiety-red-hover text-white rounded-full"
              >
{t('common.next')}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        ) : (
          // Step 2: Confirmation and Visualization
          <div className="space-y-6">
            <div className="text-center">
              <h2 className="text-xl font-bold text-black mb-2">
                {t('home.training_goal.goal_set')}
              </h2>
              <p className="text-gray-600 text-sm">
                {t('home.training_goal.confirmation_message', { count: monthlyGoal })}
              </p>
            </div>

            <div className="bg-gray-50 rounded-lg p-4 text-center">
              <h3 className="text-black font-medium mb-2">
                {t('home.training_goal.weekly_goal_title')}
              </h3>
              <div className="text-3xl font-bold text-sossiety-red mb-2">
                {weeklyGoal}
              </div>
              <p className="text-gray-600 text-sm">
                {t('home.training_goal.weekly_goal_description', { count: weeklyGoal })}
              </p>
            </div>

            <div className="flex gap-3">
              <Button
                onClick={handleBack}
                variant="outline"
                className="flex-1 border-black text-black hover:bg-gray-50 rounded-full"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
{t('common.back')}
              </Button>
              <Button
                onClick={handleConfirm}
                disabled={isCreating || isUpdating}
                className="flex-1 bg-sossiety-red hover:bg-sossiety-red-hover text-white rounded-full"
              >
{isCreating || isUpdating ? t('common.saving') : t('home.training_goal.confirm_and_start')}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
